// src/components/blog/BlogComments.jsx
// Props: postId (string), isDark (boolean)

import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Heart, Reply, Send, ChevronDown, ChevronUp, MessageCircle, Loader2 } from "lucide-react";
import { useAuth } from "../../auth/AuthContext";

/* ─── Content moderation ────────────────────────────────────── */

// Pattern-based filter: catches common slurs and severe profanity.
// Uses word-boundary anchors and covers basic leet-speak substitutions.
const BAD_PATTERNS = [
  /\bf+u+c+k+\b/i,
  /\bs+h+i+t+\b/i,
  /\bb+i+t+c+h+\b/i,
  /\ba+s+s+h+o+l+e+\b/i,
  /\bc+u+n+t\b/i,
  /\bd+i+c+k\b/i,
  /\bp+u+s+s+y\b/i,
  /\bw+h+o+r+e\b/i,
  /\bn+[i1]+g+[g3]+[e3a]+r+\b/i,
  /\bf+[a4]+g+g*[o0]+t*\b/i,
  /\br+[e3]+t+[a4]+r+d+\b/i,
  /\bs+p+[i1]+c+\b/i,
  /\bk+[i1]+k+[e3]\b/i,
  /\bc+h+[i1]+n+k+\b/i,
  /\bc+[o0]+[o0]+n\b/i,
  /\btr+[a4]+n+n+y\b/i,
];

function containsBadLanguage(text) {
  return BAD_PATTERNS.some((re) => re.test(text));
}

/* ─── Supabase ──────────────────────────────────────────────── */

const SUPA_URL = process.env.REACT_APP_SUPABASE_URL || "";
const SUPA_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || "";

function configured() { return !!(SUPA_URL && SUPA_KEY); }

function sbh(extra) {
  return { "Content-Type": "application/json", apikey: SUPA_KEY, Authorization: "Bearer " + SUPA_KEY, ...extra };
}

async function fetchComments(postId) {
  if (!configured()) return [];
  const res = await fetch(
    SUPA_URL + "/rest/v1/blog_comments?post_id=eq." + encodeURIComponent(postId) + "&order=created_at.asc&select=*",
    { headers: sbh() }
  );
  if (!res.ok) throw new Error("Could not load comments.");
  return res.json();
}

async function insertComment(postId, author, content, parentId) {
  if (!configured()) throw new Error("Comments not configured.");
  const body = { post_id: postId, author: author.trim(), content: content.trim() };
  if (parentId) body.parent_id = parentId;
  const res = await fetch(SUPA_URL + "/rest/v1/blog_comments", {
    method: "POST",
    headers: sbh({ Prefer: "return=representation" }),
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const e = await res.json().catch(() => ({}));
    throw new Error(e.message || "Failed to post.");
  }
  return res.json();
}

/* ─── Helpers ───────────────────────────────────────────────── */

function timeAgo(iso) {
  const d = Math.floor((Date.now() - new Date(iso)) / 1000);
  if (d < 60) return "just now";
  if (d < 3600) return Math.floor(d / 60) + "m ago";
  if (d < 86400) return Math.floor(d / 3600) + "h ago";
  if (d < 604800) return Math.floor(d / 86400) + "d ago";
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function avatarHue(name) { return [...name].reduce((a, c) => a + c.charCodeAt(0), 0) % 360; }
function avatarInitials(name) {
  return name.split(" ").slice(0, 2).map(w => (w[0] || "").toUpperCase()).join("");
}

const GUEST_AUTHOR_KEY = "mc_blog_guest_author";

function loadGuestAuthor() {
  try { return localStorage.getItem(GUEST_AUTHOR_KEY) || ""; } catch { return ""; }
}
function saveGuestAuthorLS(name) {
  try { localStorage.setItem(GUEST_AUTHOR_KEY, name); } catch {}
}

/* ─── Avatar ────────────────────────────────────────────────── */

function Avatar({ name, size = 28 }) {
  const s = {
    width: size, height: size, minWidth: size, borderRadius: "50%",
    background: "hsl(" + avatarHue(name) + ", 48%, 44%)",
    color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: size * 0.36, fontWeight: 700, letterSpacing: "0.04em",
    userSelect: "none", flexShrink: 0,
  };
  return <div style={s}>{avatarInitials(name) || "?"}</div>;
}

/* ─── CommentForm ───────────────────────────────────────────── */

function CommentForm({ postId, parentId, placeholder, onSuccess, onCancel, authorName, onSetAuthor }) {
  const [localName, setLocalName]   = useState("");
  const [content, setContent]       = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]           = useState("");

  // If authorName is already known (logged in or cached guest), skip the name field
  const hasAuthor = !!authorName;
  const nameToUse = hasAuthor ? authorName : localName;

  const inputStyle = {
    width: "100%", boxSizing: "border-box",
    background: "var(--blog-bg-tertiary)",
    border: "0.5px solid var(--blog-border-primary)",
    borderRadius: 6, color: "var(--blog-text-primary)",
    fontSize: 13, fontFamily: "inherit", padding: "9px 12px",
    outline: "none", lineHeight: 1.5, transition: "border-color 120ms ease",
  };
  const textareaStyle = { ...inputStyle, resize: "vertical" };
  const submitStyle = {
    display: "inline-flex", alignItems: "center", gap: 6,
    background: "var(--blog-text-primary)", color: "var(--blog-bg-primary)",
    border: "none", borderRadius: 6, padding: "8px 16px",
    fontSize: 11, fontWeight: 500, letterSpacing: "0.06em",
    fontFamily: "inherit", cursor: submitting ? "not-allowed" : "pointer",
    opacity: submitting ? 0.65 : 1, transition: "opacity 150ms ease",
  };
  const cancelStyle = {
    background: "transparent", color: "var(--blog-text-tertiary)",
    border: "0.5px solid var(--blog-border-primary)", borderRadius: 6,
    padding: "8px 14px", fontSize: 11, letterSpacing: "0.06em",
    fontFamily: "inherit", cursor: "pointer",
  };

  function submit() {
    const ta = nameToUse.trim();
    const tc = content.trim();
    if (!ta) return setError("Please enter your name.");
    if (ta.length > 80) return setError("Name must be 80 characters or fewer.");
    if (!tc) return setError("Comment can't be empty.");
    if (tc.length > 2000) return setError("Comment must be 2,000 characters or fewer.");
    if (containsBadLanguage(tc) || containsBadLanguage(ta)) {
      return setError("Your comment contains language that isn't allowed here. Please revise it.");
    }
    setSubmitting(true);
    setError("");
    insertComment(postId, ta, tc, parentId)
      .then(() => {
        // Persist guest name after first successful post
        if (!hasAuthor && localName.trim() && onSetAuthor) {
          onSetAuthor(localName.trim());
        }
        setLocalName("");
        setContent("");
        onSuccess();
      })
      .catch(e => setError(e.message))
      .finally(() => setSubmitting(false));
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {/* Only show name field if we don't already have one */}
      {!hasAuthor && (
        <input
          type="text"
          placeholder="Your name"
          value={localName}
          onChange={e => setLocalName(e.target.value)}
          maxLength={80}
          disabled={submitting}
          style={inputStyle}
        />
      )}
      {/* If name is known, show a quiet byline so the user knows who they're posting as */}
      {hasAuthor && (
        <div style={{ fontSize: 11, color: "var(--blog-text-tertiary)", letterSpacing: "0.04em" }}>
          Posting as <span style={{ color: "var(--blog-text-secondary)", fontWeight: 500 }}>{authorName}</span>
        </div>
      )}
      <textarea
        placeholder={placeholder || "Write a comment…"}
        value={content}
        onChange={e => setContent(e.target.value)}
        maxLength={2000}
        rows={parentId ? 3 : 4}
        disabled={submitting}
        style={textareaStyle}
      />
      {error && <span style={{ fontSize: 12, color: "var(--blog-accent-magenta)" }}>{error}</span>}
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <button onClick={submit} disabled={submitting} style={submitStyle}>
          {submitting
            ? <Loader2 className="w-3 h-3" style={{ animation: "spin 0.8s linear infinite" }} />
            : <Send className="w-3 h-3" />}
          {submitting ? "Posting…" : parentId ? "Reply" : "Post"}
        </button>
        {onCancel && (
          <button onClick={onCancel} disabled={submitting} style={cancelStyle}>Cancel</button>
        )}
      </div>
    </div>
  );
}

/* ─── CommentCard ───────────────────────────────────────────── */

function CommentCard({ comment, postId, replies, isDark, onRefresh, depth = 0, authorName, onSetAuthor }) {
  const [liked, setLiked]           = useState(false);
  const [localLikes, setLocalLikes] = useState(comment.likes || 0);
  const [showReply, setShowReply]   = useState(false);
  const [showReplies, setShowReplies] = useState(true);

  const isReply   = depth > 0;
  const hasReplies = replies.length > 0;
  const replyCount = replies.length;

  function toggleLike() {
    const next = !liked;
    setLiked(next);
    setLocalLikes(n => next ? n + 1 : Math.max(0, n - 1));
  }

  const bubbleStyle = {
    background: "var(--blog-bg-tertiary)",
    border: "0.5px solid var(--blog-border-secondary)",
    borderRadius: 8, padding: "10px 13px",
  };
  const likeBtnStyle = {
    display: "inline-flex", alignItems: "center", gap: 4,
    background: "transparent", border: "none", cursor: "pointer",
    fontFamily: "inherit", padding: 0, fontSize: 10, letterSpacing: "0.10em", fontWeight: 500,
    color: liked ? "var(--blog-accent-magenta)" : "var(--blog-text-tertiary)",
    transition: "color 120ms ease",
  };
  const replyBtnStyle = {
    display: "inline-flex", alignItems: "center", gap: 4,
    background: "transparent", border: "none", cursor: "pointer",
    fontFamily: "inherit", padding: 0, fontSize: 10, letterSpacing: "0.10em",
    color: showReply ? "var(--blog-accent-teal)" : "var(--blog-text-tertiary)",
    transition: "color 120ms ease",
  };
  const toggleBtnStyle = {
    display: "inline-flex", alignItems: "center", gap: 4,
    background: "transparent", border: "none", cursor: "pointer",
    fontFamily: "inherit", padding: 0, fontSize: 10, letterSpacing: "0.10em",
    color: "var(--blog-text-tertiary)", transition: "color 120ms ease",
  };

  return (
    <div>
      <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
        <Avatar name={comment.author} size={isReply ? 24 : 28} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={bubbleStyle}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 5, flexWrap: "wrap" }}>
              <span className="font-mono uppercase" style={{ fontSize: 10, letterSpacing: "0.12em", color: "var(--blog-text-primary)", fontWeight: 600 }}>
                {comment.author}
              </span>
              <span className="font-mono" style={{ fontSize: 10, color: "var(--blog-text-tertiary)" }}>
                {timeAgo(comment.created_at)}
              </span>
            </div>
            <p style={{ margin: 0, fontSize: 13, lineHeight: 1.65, color: "var(--blog-text-secondary)", wordBreak: "break-word", whiteSpace: "pre-wrap" }}>
              {comment.content}
            </p>
          </div>

          <div style={{ display: "flex", gap: 14, marginTop: 6, paddingLeft: 2 }}>
            <button onClick={toggleLike} style={likeBtnStyle} className="font-mono uppercase">
              <Heart className="w-3 h-3" style={{ fill: liked ? "var(--blog-accent-magenta)" : "none", transition: "fill 120ms ease" }} />
              {liked ? "Liked" : "Like"}
              {localLikes > 0 && <span style={{ opacity: 0.6, marginLeft: 2 }}>{localLikes}</span>}
            </button>

            {depth === 0 && (
              <button onClick={() => setShowReply(v => !v)} style={replyBtnStyle} className="font-mono uppercase">
                <Reply className="w-3 h-3" />
                {showReply ? "Cancel" : "Reply"}
              </button>
            )}

            {hasReplies && depth === 0 && (
              <button onClick={() => setShowReplies(v => !v)} style={toggleBtnStyle} className="font-mono uppercase">
                {showReplies ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                {showReplies ? "Hide replies" : replyCount + (replyCount === 1 ? " reply" : " replies")}
              </button>
            )}
          </div>

          <AnimatePresence>
            {showReply && (
              <motion.div
                key="rf"
                initial={{ opacity: 0, height: 0, marginTop: 0 }}
                animate={{ opacity: 1, height: "auto", marginTop: 10 }}
                exit={{ opacity: 0, height: 0, marginTop: 0 }}
                transition={{ duration: 0.18 }}
                style={{ overflow: "hidden" }}
              >
                <CommentForm
                  postId={postId}
                  parentId={comment.id}
                  placeholder={"Reply to " + comment.author + "…"}
                  isDark={isDark}
                  onSuccess={() => { setShowReply(false); onRefresh(); }}
                  onCancel={() => setShowReply(false)}
                  authorName={authorName}
                  onSetAuthor={onSetAuthor}
                />
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {showReplies && hasReplies && (
              <motion.div
                key="replies"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.18 }}
                style={{ overflow: "hidden" }}
              >
                <div style={{ marginTop: 12, paddingLeft: 12, borderLeft: "1.5px solid var(--blog-border-secondary)", display: "flex", flexDirection: "column", gap: 10 }}>
                  {replies.map(r => (
                    <CommentCard
                      key={r.id}
                      comment={r}
                      postId={postId}
                      replies={[]}
                      isDark={isDark}
                      onRefresh={onRefresh}
                      depth={1}
                      authorName={authorName}
                      onSetAuthor={onSetAuthor}
                    />
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

/* ─── Skeleton ──────────────────────────────────────────────── */

function Skeleton() {
  const barStyle = {
    height: 68, borderRadius: 8,
    background: "var(--blog-bg-tertiary)",
    border: "0.5px solid var(--blog-border-secondary)",
    animation: "pulse 1.4s ease-in-out infinite",
  };
  const circleStyle = { width: 28, height: 28, minWidth: 28, borderRadius: "50%", background: "var(--blog-bg-tertiary)" };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", gap: 10 }}><div style={circleStyle} /><div style={{ flex: 1 }}><div style={barStyle} /></div></div>
      <div style={{ display: "flex", gap: 10 }}><div style={circleStyle} /><div style={{ flex: 1 }}><div style={barStyle} /></div></div>
    </div>
  );
}

/* ─── BlogComments (main export) ────────────────────────────── */

export function BlogComments({ postId, isDark = false }) {
  const { user }  = useAuth();
  const [comments, setComments] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState("");

  // Guest name persisted in localStorage; cleared once the user logs in
  const [guestName, setGuestName] = useState(() => loadGuestAuthor());

  function saveGuestName(name) {
    setGuestName(name);
    saveGuestAuthorLS(name);
  }

  // Logged-in user always takes priority over cached guest name
  const authorName = user ? (user.name || user.email) : guestName;

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    return fetchComments(postId)
      .then(data => setComments(data))
      .catch(() => setError("Couldn't load comments. Try refreshing."))
      .finally(() => setLoading(false));
  }, [postId]);

  useEffect(() => { load(); }, [load]);

  const topLevel  = comments.filter(c => !c.parent_id);
  const repliesFor = id => comments.filter(c => c.parent_id === id);

  const formBoxStyle = {
    marginBottom: 28, padding: 16,
    background: "var(--blog-bg-secondary)",
    border: "0.5px solid var(--blog-border-primary)",
    borderRadius: 8,
  };

  if (!configured()) {
    return (
      <div style={{ padding: "20px 0", fontSize: 12, color: "var(--blog-text-tertiary)", letterSpacing: "0.06em" }}>
        Comments not configured  -  set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY in your .env.
      </div>
    );
  }

  return (
    <div>
      {!loading && !error && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 20 }}>
          <MessageCircle className="w-3.5 h-3.5" style={{ color: "var(--blog-text-tertiary)" }} />
          <span className="font-mono" style={{ fontSize: 11, color: "var(--blog-text-tertiary)", letterSpacing: "0.06em" }}>
            {comments.length === 0
              ? "No comments yet"
              : comments.length + (comments.length === 1 ? " comment" : " comments")}
          </span>
        </div>
      )}

      <div style={formBoxStyle}>
        <div className="font-mono uppercase" style={{ fontSize: 9, letterSpacing: "0.12em", color: "var(--blog-text-tertiary)", marginBottom: 12 }}>
          Leave a comment
        </div>
        <CommentForm
          postId={postId}
          placeholder="Share your thoughts…"
          isDark={isDark}
          onSuccess={load}
          authorName={authorName}
          onSetAuthor={saveGuestName}
        />
      </div>

      {loading && <Skeleton />}
      {error && !loading && <p style={{ fontSize: 13, color: "var(--blog-accent-magenta)" }}>{error}</p>}

      {!loading && !error && topLevel.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {topLevel.map(c => (
            <CommentCard
              key={c.id}
              comment={c}
              postId={postId}
              replies={repliesFor(c.id)}
              isDark={isDark}
              onRefresh={load}
              authorName={authorName}
              onSetAuthor={saveGuestName}
            />
          ))}
        </div>
      )}

      <style>{`@keyframes spin{to{transform:rotate(360deg)}}@keyframes pulse{0%,100%{opacity:1}50%{opacity:.45}}`}</style>
    </div>
  );
}
